import React from 'react'
import { graphql } from 'gatsby'
import { v4 as uuid } from 'uuid'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import MastHead from '../components/organisms/MastHead/masthead'
import ReviewCarousel from '../components/organisms/GalleryCarousel/ReviewCarousel'
import TextHeader from '../components/organisms/TextHeader/TextHeader'
import CalloutCtA from '../components/organisms/CalloutCTA/CalloutCTA'
import getCalloutLink from '../helpers/getCalloutLink'
import isOuterLink from '../helpers/isOuterLink'
import TypographyAtom from '../components/atoms/typography/typography'
import SectionHeader from '../components/organisms/SectionHeader/SectionHeader'
import Accordion from '../components/organisms/Accordion/Accordion'
import SummaryCard from '../components/organisms/SummaryCard/SummaryCard'
import SummaryCardList from '../components/organisms/SummaryCardList/SummaryCardList'
import ProductCarousel from '../components/organisms/GalleryCarousel/ProductCarousel'
import { createWebpageSchema } from '../helpers/createWebpageSchema'
import Breadcrumbs from '../components/organisms/Breadcrumbs/Breadcrumbs'
import { createFAQSchema } from '../helpers/createFAQSchema'
import Lockup from '../components/organisms/TextAndImageLockup/TextAndImageLockup'
import LinkAtom from '../components/atoms/links/links'
import BlogArticleCard from '../components/organisms/BlogArticleCard/BlogArticleCard'
import { ROUTES } from '../constants/routes'
import IndustryCarousel from '../components/organisms/GalleryCarousel/IndustryCarousel'
import DroneOperationsCard from '../components/organisms/DroneOperationsCard/DroneOperationsCard'
import MetaTwitterCard from '../components/organisms/MetaTwitterCard/MetaTwitterCard'

export function Head({ data, pageContext, location }) {
  const pageMetaTitle = data?.contentfulPageIndustrySubLevel?.pageMetaTitle || ''
  const pageMetaDescription = data?.contentfulPageIndustrySubLevel?.pageMetaDescription || ''
  const ogTitle = data?.contentfulPageIndustrySubLevel?.mastheadTitleLine || pageMetaTitle

  const faqSchema = createFAQSchema(data.contentfulPageIndustrySubLevel?.frequentlyAskedQuestion)
  const webPageSchema = createWebpageSchema({
    pageTitle: pageMetaTitle,
    pageDescription: pageMetaDescription,
    pageUrl: `${process.env.GATSBY_HELIGUY_DOMAIN}${location?.pathname}`,
    breadcrumbs: pageContext?.breadCrumbs,
  })

  const pageObjectUrl =
    data?.contentfulPageIndustrySubLevel?.mastheadBackgroundImage?.url ||
    'https://placehold.co/400x400'
  const pageObjectUrlWidth =
    data?.contentfulPageIndustrySubLevel?.mastheadBackgroundImage?.gatsbyImageData?.width || '400'
  const pageObjectUrlHeight =
    data?.contentfulPageIndustrySubLevel?.mastheadBackgroundImage?.gatsbyImageData?.height || '400'

  return (
    <>
      <html lang="en" />
      <meta charset="UTF-8" />
      <title>{pageMetaTitle}</title>
      <meta name="description" content={pageMetaDescription} />
      <meta name="keywords" content="" />
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Heliguy" />
      <meta property="og:locale" content="en" />
      <meta property="og:site_name" content="Heliguy™" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={pageMetaDescription} />
      <meta property="og:image" content={pageObjectUrl} />
      <meta property="og:image:width" content={pageObjectUrlWidth} />
      <meta property="og:image:height" content={pageObjectUrlHeight} />
      <meta
        property="og:url"
        content={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
      />
      <MetaTwitterCard
        title={ogTitle}
        mainImage={pageObjectUrl}
        pageUrl={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
        pageDescription={pageMetaDescription}
      />
      <link rel="preconnect" href="https://images.ctfassets.net" crossOrigin />
      <link rel="preconnect" href="https://static-tracking.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://static.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://fast.a.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://static-forms.klaviyo.com" crossOrigin />
      <link rel="preconnect" href="https://heliguy.myshopify.com" crossOrigin />
      <link rel="preconnect" href="https://apis.google.com" crossOrigin />
      <link
        rel="canonical"
        href={`${process.env.GATSBY_HELIGUY_DOMAIN}${pageContext.breadcrumb.location}`}
      />
      {webPageSchema && <script type="application/ld+json">{webPageSchema}</script>}
      {faqSchema && <script type="application/ld+json">{faqSchema}</script>}
    </>
  )
}

function industrySubLevelTemplate({ data, pageContext }) {
  const contentfulId = data?.contentfulPageIndustrySubLevel?.contentful_id || ''
  const mastheadTitleLine = data?.contentfulPageIndustrySubLevel?.mastheadTitleLine || ''
  const mastheadSubtitleLine = data?.contentfulPageIndustrySubLevel?.mastheadSubtitle || ''
  const mastheadTitleImage =
    data?.contentfulPageIndustrySubLevel?.mastheadBackgroundImage?.gatsbyImageData || ''
  const introTextBlock = data?.contentfulPageIndustrySubLevel?.introTextBlock || ''
  const partnersReviews = data?.contentfulPageIndustrySubLevel?.partnersReviews || []
  const reviewsSectionTitle = data?.contentfulPageIndustrySubLevel?.reviewsSectionTitle || ''
  const reviewsSectionSubtitle = data?.contentfulPageIndustrySubLevel?.reviewsSectionSubtitle || ''
  const ctaCalloutTitle = data?.contentfulPageIndustrySubLevel?.ctaCalloutTitle || ''
  const ctaCalloutBody = data?.contentfulPageIndustrySubLevel?.ctaCalloutBody || ''
  const ctaButtonHref = data?.contentfulPageIndustrySubLevel?.ctaButtonHref || ''
  const ctaCalloutButtonText = data?.contentfulPageIndustrySubLevel?.ctaCalloutButtonText || ''
  const frequentlyAskedQuestion =
    data?.contentfulPageIndustrySubLevel?.frequentlyAskedQuestion || []
  const pageSummarySectionHeader =
    data?.contentfulPageIndustrySubLevel?.pageSummarySectionHeader || ''
  const pageSummarySectionSubtitle =
    data?.contentfulPageIndustrySubLevel?.pageSummarySectionSubtitle || ''
  const pageSummaryCardOneTitle =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardOneTitle || ''
  const pageSummaryCardOneBody = data?.contentfulPageIndustrySubLevel?.pageSummaryCardOneBody || ''
  const pageSummaryCardTwoTitle =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardTwoTitle || ''
  const pageSummaryCardTwoBody = data?.contentfulPageIndustrySubLevel?.pageSummaryCardTwoBody || ''
  const pageSummaryCardThreeTitle =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardThreeTitle || ''
  const pageSummaryCardThreeBody =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardThreeBody || ''
  const pageSummaryCardFourTitle =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardFourTitle || ''
  const pageSummaryCardFourBody =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardFourBody || ''
  const pageSummaryCardFiveTitle =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardFiveTitle || ''
  const pageSummaryCardFiveBody =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardFiveBody || ''
  const pageSummaryCardSixTitle =
    data?.contentfulPageIndustrySubLevel?.pageSummaryCardSixTitle || ''
  const pageSummaryCardSixBody = data?.contentfulPageIndustrySubLevel?.pageSummaryCardSixBody || ''
  const blogList = data?.contentfulPageIndustrySubLevel?.blogList || []
  const servicesGroup = data?.contentfulPageIndustrySubLevel?.servicesGroup || []
  const productSliderTitle = data?.contentfulPageIndustrySubLevel?.productSliderTitle || ''
  const productSliderSubtitle = data?.contentfulPageIndustrySubLevel?.productSliderSubtitle || ''
  const sliderCategoryOneTitle = data?.contentfulPageIndustrySubLevel?.sliderCategoryOneTitle || ''
  const sliderCategoryTwoTitle = data?.contentfulPageIndustrySubLevel?.sliderCategoryTwoTitle || ''
  const sliderCategoryThreeTitle =
    data?.contentfulPageIndustrySubLevel?.sliderCategoryThreeTitle || ''
  const sliderCategoryOneProductList =
    data?.sliderCategoryOneProductList.edges.map(({ node }) => ({ ...node })) || []
  const sliderCategoryTwoProductList =
    data?.sliderCategoryTwoProductList.edges.map(({ node }) => ({ ...node })) || []
  const sliderCategoryThreeProductList =
    data?.sliderCategoryThreeProductList.edges.map(({ node }) => ({ ...node })) || []

  const isSliderTabOne = sliderCategoryOneTitle && sliderCategoryOneProductList.length > 0
  const isSliderTabTwo = sliderCategoryTwoTitle && sliderCategoryTwoProductList.length > 0
  const isSliderTabThree = sliderCategoryThreeTitle && sliderCategoryThreeProductList.length > 0
  const isProductSlider = isSliderTabOne && isSliderTabTwo && isSliderTabThree
  const droneOperationsTitle =
    data?.contentfulPageIndustrySubLevel?.droneOperationsTitle ||
    'Drone Operations in the Real World'
  const droneOperationsSubtitle =
    data?.contentfulPageIndustrySubLevel?.droneOperationsSubtitle ||
    'See how our drones perform on actual construction projects'
  const partnerLogoList = data?.contentfulPageIndustrySubLevel?.partnerLogoList || []
  const droneOperationsContentList =
    data?.contentfulPageIndustrySubLevel?.droneOperationsContentList || []

  const summaryCards = [
    {
      id: uuid(),
      title: pageSummaryCardOneTitle,
      content: pageSummaryCardOneBody,
      contentFieldId: 'pageSummaryCardOneBody',
    },
    {
      id: uuid(),
      title: pageSummaryCardTwoTitle,
      content: pageSummaryCardTwoBody,
      contentFieldId: 'pageSummaryCardTwoBody',
    },
    {
      id: uuid(),
      title: pageSummaryCardThreeTitle,
      content: pageSummaryCardThreeBody,
      contentFieldId: 'pageSummaryCardThreeBody',
    },
    {
      id: uuid(),
      title: pageSummaryCardFourTitle,
      content: pageSummaryCardFourBody,
      contentFieldId: 'pageSummaryCardFourBody',
    },
    {
      id: uuid(),
      title: pageSummaryCardFiveTitle,
      content: pageSummaryCardFiveBody,
      contentFieldId: 'pageSummaryCardfiveBody',
    },
    {
      id: uuid(),
      title: pageSummaryCardSixTitle,
      content: pageSummaryCardSixBody,
      contentFieldId: 'pageSummaryCardSixBody',
    },
  ]

  const calloutButtonLink = getCalloutLink(ctaButtonHref)

  const isPartiallyActive = ({ isPartiallyCurrent, isCurrent }) =>
    isPartiallyCurrent && isCurrent
      ? { className: 'breadcrumb__link breadcrumb__link__active' }
      : {}

  return (
    <div className="templateWrapper templateWrapper--alt">
      <section className="outer">
        <div className="inner">
          <Breadcrumbs
            crumbs={pageContext.breadCrumbs}
            crumbSeparator=" - "
            getProps={isPartiallyActive}
          />
        </div>
      </section>
      <MastHead
        className="margin-160"
        type="Primary"
        imgSrc={mastheadTitleImage}
        title={mastheadTitleLine}
        subtitle={mastheadSubtitleLine}
      />
      <section className="outer margin-160">
        <div className="inner">
          <div className="row between-md">
            <div className="col-xs-12 col-md-7">
              <TypographyAtom type="BlockText" copyBlock={introTextBlock} weight="BlockText" />
            </div>
            <aside className="col-xs-12 col-md-4">
              <CalloutCtA
                fieldsNames={{
                  title: 'ctaCalloutTitle',
                  body: 'ctaCalloutBody',
                  button: 'ctaCalloutButtonText',
                }}
                title={ctaCalloutTitle}
                body={ctaCalloutBody}
                buttonCopy={ctaCalloutButtonText}
                linkType={isOuterLink(calloutButtonLink) ? '_blank' : '_self'}
                rel={isOuterLink(calloutButtonLink) ? 'noopener noreferrer' : ''}
                linkUrl={calloutButtonLink}
              />
            </aside>
          </div>
        </div>
      </section>
      {droneOperationsContentList.length > 0 && (
        <section className="outer margin-160">
          <div className="inner">
            <TextHeader title={droneOperationsTitle} subtitle={droneOperationsSubtitle} />
            <div className="row between-md gap-24-sm gap-0-md">
              {droneOperationsContentList.map((card) => (
                <div key={card?.contentful_id} className="col-xs-12 col-md-6">
                  <DroneOperationsCard
                    title={card?.title}
                    description={card?.body}
                    posterImage={card?.image}
                    youTubeLink={card?.youTubeLink}
                    videoFile={card?.video?.file?.url}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <section className="outer margin-160">
        <div className="inner">
          <TextHeader title={pageSummarySectionHeader} subtitle={pageSummarySectionSubtitle} />
          <SummaryCardList>
            {summaryCards.map(({ id, title, content, contentFieldId }) => (
              <li key={id}>
                <SummaryCard
                  contentful_id={contentfulId}
                  title={title}
                  content={content}
                  contentFieldId={contentFieldId}
                />
              </li>
            ))}
          </SummaryCardList>
        </div>
      </section>
      <section className="outer margin-160">
        <div className="inner">
          {servicesGroup.map((target) => (
            <Lockup
              key={target.contentful_id}
              contentful_id={target.contentful_id}
              fieldsNames={{
                heading: 'title',
                subheading: 'subtitle',
                body: 'body',
                button: 'ctaText',
                image: 'image',
              }}
              direction={target.direction}
              imgSrc={target.image.gatsbyImageData}
              imgAltTag={target.image.description}
              heading={target.title}
              subheader={target.subtitle}
              body={target.body}
              linkDestination={target.ctaSlug}
              linkText={target.ctaText}
              id={target.anchor}
            />
          ))}
        </div>
      </section>
      <section className="outer margin-160 limited">
        <div className="inner">
          <TextHeader title={reviewsSectionTitle} subtitle={reviewsSectionSubtitle} />
          <ReviewCarousel reviews={partnersReviews} />
        </div>
      </section>
      <section className="outer margin-160">
        <div className="inner">
          <IndustryCarousel carouselImages={partnerLogoList} />
        </div>
      </section>
      {blogList.length > 0 && (
        <section className="outer margin-160">
          <div className="inner">
            <TextHeader
              title="Latest From the Blog"
              subtitle="Explore how our technology drives smarter decisions and better outcomes."
            />
            <div className="row row-gap-xs row-gap-md row-gap-lg margin-32 margin-md-32 margin-lg-56">
              {blogList.map((blog) => (
                <div key={blog?.id} className="col-xs-12 col-sm-6 col-lg-4">
                  <BlogArticleCard
                    linkDestination={`posts/${blog?.pageSlug}`}
                    imgSrc={blog?.posterImage?.gatsbyImageData}
                    altTag={blog?.articleTitle}
                    articleCategory={blog?.articleCategory}
                    header={blog?.articleTitle}
                    articleShortDescription={blog?.articleShortDescription}
                    articleAuthor={blog?.articleAuthor}
                    updatedDate={blog?.updatedDate}
                    publishedDate={blog?.publishedDate}
                  />
                </div>
              ))}
            </div>
            <LinkAtom
              linkType="Simple Button Link"
              linkDestination={ROUTES.BLOG}
              linkText="View all articles"
            />
          </div>
        </section>
      )}
      {isProductSlider && (
        <section className="outer margin-160">
          <div className="inner">
            <TextHeader title={productSliderTitle} subtitle={productSliderSubtitle} />
            <Tabs>
              <TabList>
                {isSliderTabOne && <Tab>{sliderCategoryOneTitle}</Tab>}
                {isSliderTabTwo && <Tab>{sliderCategoryTwoTitle}</Tab>}
                {isSliderTabThree && <Tab>{sliderCategoryThreeTitle}</Tab>}
              </TabList>
              {isSliderTabOne && (
                <TabPanel>
                  <ProductCarousel carouselImages={sliderCategoryOneProductList} nonFooter />
                </TabPanel>
              )}
              {isSliderTabTwo && (
                <TabPanel>
                  <ProductCarousel carouselImages={sliderCategoryTwoProductList} nonFooter />
                </TabPanel>
              )}
              {isSliderTabThree && (
                <TabPanel>
                  <ProductCarousel carouselImages={sliderCategoryThreeProductList} nonFooter />
                </TabPanel>
              )}
            </Tabs>
          </div>
        </section>
      )}
      {frequentlyAskedQuestion.length > 0 && (
        <section className="outer">
          <div className="inner">
            <SectionHeader headerCopy="Frequently Asked Questions" />
            <ul className="list">
              {frequentlyAskedQuestion.map(({ contentful_id, faqQuestion, faqAnswer }) => (
                <li className="margin-md" key={contentful_id}>
                  <Accordion
                    fieldsNames={{
                      header: 'faqQuestion',
                      body: 'faqAnswer',
                    }}
                    contentful_id={contentful_id}
                    header={faqQuestion}
                    body={faqAnswer}
                  />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  )
}

export default industrySubLevelTemplate

export const data = graphql`
  query (
    $id: String!
    $productHandleSliderOneArray: [String]
    $productHandleSliderTwoArray: [String]
    $productHandleSliderThreeArray: [String]
  ) {
    contentfulPageIndustrySubLevel(contentful_id: { eq: $id }) {
      contentful_id
      pageSlug
      pageMetaTitle
      pageMetaDescription
      mastheadTitleLine
      mastheadSubtitle
      mastheadBackgroundImage {
        gatsbyImageData(formats: WEBP)
        url
      }
      introTextBlock {
        raw
      }
      ctaCalloutTitle
      ctaCalloutBody {
        raw
      }
      ctaButtonHref
      ctaCalloutButtonText
      droneOperationsTitle
      droneOperationsSubtitle
      droneOperationsContentList {
        contentful_id
        title
        body {
          raw
        }
        image {
          title
          gatsbyImageData(formats: WEBP)
        }
        video {
          file {
            url
          }
        }
        youTubeLink
      }
      reviewsSectionTitle
      reviewsSectionSubtitle
      pageSummarySectionHeader
      pageSummarySectionSubtitle
      pageSummaryCardOneTitle
      pageSummaryCardOneBody
      pageSummaryCardTwoTitle
      pageSummaryCardTwoBody
      pageSummaryCardThreeTitle
      pageSummaryCardThreeBody
      pageSummaryCardFourTitle
      pageSummaryCardFourBody
      pageSummaryCardFiveTitle
      pageSummaryCardFiveBody
      pageSummaryCardSixTitle
      pageSummaryCardSixBody
      servicesGroup {
        contentful_id
        title
        anchor
        subtitle
        direction
        ctaSlug
        ctaText
        body {
          raw
        }
        image {
          description
          gatsbyImageData(formats: WEBP, width: 900)
        }
      }
      partnersReviews {
        id
        contentful_id
        reviewContent {
          raw
        }
        authors
        positionAndCompany
        partnerLogo {
          id
          title
          url
          gatsbyImageData(width: 360)
        }
      }
      partnerLogoList {
        id
        title
        url
      }
      blogList {
        id
        pageSlug
        articleTitle
        articleShortDescription
        articleCategory
        articleAuthor
        publishedDate
        updatedDate
        snippetYouTubeLink
        snippetVideoFile {
          file {
            url
          }
        }
        posterImage {
          url
          description
          gatsbyImageData(formats: WEBP, width: 1000)
        }
      }
      productSliderTitle
      productSliderSubtitle
      sliderCategoryOneTitle
      sliderCategoryTwoTitle
      sliderCategoryThreeTitle
      frequentlyAskedQuestion {
        contentful_id
        faqQuestion
        faqAnswer {
          raw
        }
      }
    }
    sliderCategoryOneProductList: allShopifyProduct(
      filter: { handle: { in: $productHandleSliderOneArray } }
    ) {
      edges {
        node {
          title
          shopifyId
          handle
          productType
          metafield(key: "excerpt", namespace: "content") {
            id
            value
          }
          featuredImage {
            gatsbyImageData(formats: WEBP, width: 900)
            altText
          }
        }
      }
    }
    sliderCategoryTwoProductList: allShopifyProduct(
      filter: { handle: { in: $productHandleSliderTwoArray } }
    ) {
      edges {
        node {
          title
          shopifyId
          handle
          productType
          metafield(key: "excerpt", namespace: "content") {
            id
            value
          }
          featuredImage {
            gatsbyImageData(formats: WEBP, width: 900)
            altText
          }
        }
      }
    }
    sliderCategoryThreeProductList: allShopifyProduct(
      filter: { handle: { in: $productHandleSliderThreeArray } }
    ) {
      edges {
        node {
          title
          shopifyId
          handle
          productType
          metafield(key: "excerpt", namespace: "content") {
            id
            value
          }
          featuredImage {
            gatsbyImageData(formats: WEBP, width: 900)
            altText
          }
        }
      }
    }
  }
`
